import { Storage } from "aws-amplify";
import * as React from "react";
import { useDispatch } from "redux-react-hook";
import styled from "styled-components";
import DOMPurify from "dompurify";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import { ThemeType } from "src/library/utils/theme";
import { Logo } from "src/core/shared/components/Logo";
import {
  ISettingsResponse,
  UpdateGlobalSettingParams,
  useGlobalSettingsApi
} from "src/core/shared/api/globalSettings.api";
import { setAppSettings } from "src/core/shared/store/global/global.actions";

import Setting from "./Setting";

interface ThemeLogoUploadProps {
  type: "light" | "dark";
  description: string;
}

const ThemeLogoUploadSetting: React.ReactFCWithChildren<ThemeLogoUploadProps> = ({ type, description }) => {
  const [ globalSettings, globalSettingsApi ] = useGlobalSettingsApi();
  const [ updateGlobalSettingsResponse, updateGlobalSettings ] = globalSettingsApi<ISettingsResponse<"theme", ThemeType>>("UPDATE_SETTING");

  // update theme in db
  const updateCustomLogo = (logoPath: string) => {
    if (globalSettings && globalSettings.theme) {
      // clone theme object
      const updatedTheme: ThemeType = { ...globalSettings.theme };
      const logoKey = type === "light" ? "customLightLogo" : "customDarkLogo";

      // update custom logo
      updatedTheme[ logoKey ] = logoPath;

      // push updated theme to db
      updateGlobalSettings<UpdateGlobalSettingParams<ThemeType>>({
        name: "theme",
        data: updatedTheme
      });
    }
  };

  const dispatch = useDispatch();

  // on successful theme change in db, update redux store
  React.useEffect(() => {
    if (globalSettings && updateGlobalSettingsResponse.data) {
      const updatedTheme: ThemeType = updateGlobalSettingsResponse.data.setting;

      dispatch(
        setAppSettings({
          ...globalSettings,
          theme: updatedTheme
        })
      );
    }
  }, [ updateGlobalSettingsResponse.data ]);

  // upload state
  const [ isUploading, setIsUploading ] = React.useState<boolean>(false);
  // error state
  const [ uploadError, setUploadError ] = React.useState<boolean>(false);

  // upload selected logo to S3
  const onImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // clear errors
      setUploadError(false);
      // trigger load spinner
      setIsUploading(true);
      // get File object
      let imageFile = e.target.files[ 0 ];
      const type = imageFile.type;

      if (imageFile) {
        // santitize svgs on upload
        if (imageFile.type === "image/svg+xml") {
          const body = await imageFile.text();
          const clean = DOMPurify.sanitize(body);

          // replace the image file with a clean version
          imageFile = new File([ clean ], imageFile.name, { type });
        }

        try {
          // upload to S3
          const result: any = await Storage.put(`assets/${Date.now()}-${type}-logo`, imageFile, {
            cacheControl: "max-age=31536000", // 1 year cache
            level: "public",
            contentType: type
          });

          // add to theme
          updateCustomLogo(result.key);
        } catch (error) {
          console.error((error as Error).message);
          // toggle error state
          setUploadError(true);
        } finally {
          // hide load spinner
          setIsUploading(false);
        }
      }
    }
  };

  const logoPickerRef = React.useRef<HTMLInputElement>(null);

  const triggerLogoPicker = () => {
    if (logoPickerRef.current) {
      logoPickerRef.current.click();
    }
  };

  return (
    <Setting description={description}>
      <Grid.Bounds
        direction="horizontal"
        style={{ alignItems: "center" }}
      >
        <LogoWrapper>
          <Logo type={type} />
        </LogoWrapper>

        <Button
          background="darkGrey"
          $buttonType="plain"
          color="white"
          onClick={triggerLogoPicker}
          loading={isUploading}
        >
          Choose
        </Button>

        <input
          ref={logoPickerRef}
          type="file"
          hidden
          onChange={onImageSelect}
          accept="image/svg+xml, image/png, image/jpeg"
        />
      </Grid.Bounds>

      {uploadError && (
        <ErrorWrapper>
          <ErrorText>
            An error occurred whilst uploading. Please try again!
          </ErrorText>
        </ErrorWrapper>
      )}
    </Setting>
  );
};

const LogoWrapper = styled.div`
  height: 72px;
  min-width: 72px;
  position: relative;
  margin-right: 18px;

  img {
    height: 100% !important;
    width: auto !important;
  }
`;

const ErrorWrapper = styled.div`
  margin-top: 12px;
`;

const ErrorText = styled(Text)`
  color: ${({ theme }) => theme.colors.error};
`;

export default ThemeLogoUploadSetting;
